import { makeAutoObservable, runInAction } from 'mobx'
import { RootStore } from '@/app/store'
import { toast } from 'react-toastify'
import {
  VoiceChatState,
  AudioState,
  VoiceChatOptions,
  VoiceChatSession,
  VoiceChatStoreInterface,
  VoiceChatEventType,
  WebRTCAssistantResponseDeltaEvent,
  WebRTCErrorEvent,
  WebRTCAssistantResponseDoneEvent,
  WebRTCUserTranscriptionCompletedEvent,
  WebRTCAssistantResponseStartedEvent,
  WebRTCSpeechCapturedEvent,
  WebRTCSpeakingEndedEvent,
} from './types'
import { createVoiceChatSession, endVoiceChatSession } from './api'
import { VoiceModeWebRTCClient } from './voice-webrtc-client'

export class VoiceChatStore implements VoiceChatStoreInterface {
  rootStore: RootStore
  state: VoiceChatState = VoiceChatState.IDLE
  audioState: AudioState = AudioState.INACTIVE
  currentSession: VoiceChatSession | null = null
  error: string | null = null

  private webRTCClient: VoiceModeWebRTCClient | null = null

  // Maps event identifiers (role-eventId) to message indices in the chat store
  // This prevents race conditions when handling multiple overlapping transcriptions:
  // 1. When user interrupts assistant, we could have multiple messages in-flight
  // 2. WebRTC events might arrive out of order or with overlapping timing
  // 3. Each audio segment needs a unique identifier to link it to the correct message
  // 4. Combined keys (role-eventId) make debugging easier by providing context
  private voiceThreadMessageByEvent: Record<
    string,
    { index: number; fullMessage: string }
  > = {}

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  get isActive(): boolean {
    return this.state === VoiceChatState.ACTIVE
  }

  get isInitializing(): boolean {
    return (
      this.state === VoiceChatState.CONNECTING ||
      this.state === VoiceChatState.INITIALIZING ||
      this.state === VoiceChatState.REQUESTING_PERMISSIONS
    )
  }

  get isAssistantSpeaking(): boolean {
    return this.audioState === AudioState.SPEAKING
  }

  get isUserSpeaking(): boolean {
    return this.audioState === AudioState.LISTENING
  }

  private async askForMicPermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      })
      stream.getTracks().forEach((track) => track.stop())
      return true
    } catch (error) {
      console.error('Error asking for mic permission:', error)
      return false
    }
  }

  async startSession(options: VoiceChatOptions): Promise<boolean> {
    if (this.isInitializing || this.isActive) {
      console.warn('Voice chat session already active or initializing')
      return false
    }

    if (!options.toolId) {
      console.error('Tool ID is required to start a voice chat session')
      runInAction(() => {
        this.error = 'Tool ID is required'
        this.state = VoiceChatState.ERROR
      })
      return false
    }

    try {
      runInAction(() => {
        this.state = VoiceChatState.INITIALIZING
      })

      const { chat } = this.rootStore

      if (!chat.thread?._id) {
        await chat.maybeCreateThread(options.toolId)
      }

      const threadId = chat.thread._id

      if (!threadId) {
        runInAction(() => {
          this.state = VoiceChatState.ERROR
          this.error = 'Error creating thread'
        })
        return false
      }

      const micPermission = await this.askForMicPermission()

      if (!micPermission) {
        toast.error('No se pudo acceder al micrófono')

        runInAction(() => {
          this.state = VoiceChatState.ERROR
          this.error = 'Error asking for mic permission'
        })

        return false
      }

      const sessionData = await createVoiceChatSession(options.toolId, threadId)
      const { sessionId, rtcToken, rtcServer } = sessionData

      runInAction(() => {
        this.currentSession = {
          id: sessionId,
          active: true,
          startTime: Date.now(),
          rtcToken,
        }
        this.state = VoiceChatState.CONNECTING
        this.voiceThreadMessageByEvent = {}
      })

      this.webRTCClient = new VoiceModeWebRTCClient()
      this.registerEventListeners(this.webRTCClient)

      const connected = await this.webRTCClient.connect(rtcServer, rtcToken)

      console.log('connected to openai webrtc server', connected)

      if (!connected) {
        throw new Error('Failed to connect to OpenAI WebRTC server')
      }

      runInAction(() => {
        this.state = VoiceChatState.ACTIVE
        this.audioState = AudioState.LISTENING
      })

      return true
    } catch (error) {
      console.error('Failed to start voice chat session:', error)

      runInAction(() => {
        this.state = VoiceChatState.ERROR
        this.error =
          error instanceof Error
            ? error.message
            : 'Unknown error starting voice chat session'
      })

      toast.error('No se pudo iniciar la sesión de voz')

      this.cleanupWebRTCClient()

      return false
    }
  }

  async endSession(): Promise<void> {
    if (this.state !== VoiceChatState.ACTIVE || !this.currentSession) {
      return
    }

    try {
      // Finalize any active streaming messages
      this.finalizeActiveStreamingMessages()

      // Persist session messages
      if (this.currentSession) {
        runInAction(() => {
          this.rootStore.tool.generating = false
        })

        await this.rootStore.chat.persistUncommittedMessages()
        await endVoiceChatSession(this.currentSession.id)
      }

      if (this.webRTCClient) {
        this.cleanupWebRTCClient()
      }
    } catch (error) {
      console.error('Error ending voice chat session:', error)
    } finally {
      this.resetState()
    }
  }

  resetState(): void {
    this.state = VoiceChatState.IDLE
    this.audioState = AudioState.INACTIVE
    this.currentSession = null
    this.error = null
    this.voiceThreadMessageByEvent = {}
    this.rootStore.tool.generating = false
  }

  private finalizeActiveStreamingMessages(): void {
    const { chat } = this.rootStore

    Object.values(this.voiceThreadMessageByEvent).forEach(
      ({ index, fullMessage }) => {
        chat.commitMessage(index, fullMessage)
      },
    )
  }

  private registerEventListeners(webRTCClient: VoiceModeWebRTCClient): void {
    // Listening state events
    webRTCClient.addEventListener(
      VoiceChatEventType.SPEECH_CAPTURED,
      this.handleSpeechCapturedEvent,
    )

    // User transcription events
    webRTCClient.addEventListener(
      VoiceChatEventType.USER_TRANSCRIPTION_COMPLETED,
      this.handleUserTranscriptionCompletedEvent,
    )

    webRTCClient.addEventListener(
      VoiceChatEventType.ASSISTANT_RESPONSE_STARTED,
      this.handleAssistantResponseStartedEvent,
    )

    webRTCClient.addEventListener(
      VoiceChatEventType.ASSISTANT_RESPONSE_DELTA,
      this.handleAssistantResponseDeltaEvent,
    )

    webRTCClient.addEventListener(
      VoiceChatEventType.ASSISTANT_RESPONSE_DONE,
      this.handleAssistantResponseDoneEvent,
    )

    // Speaking state events
    webRTCClient.addEventListener(
      VoiceChatEventType.SPEAKING_STARTED,
      this.handleSpeakingStartedEvent,
    )

    webRTCClient.addEventListener(
      VoiceChatEventType.SPEAKING_ENDED,
      this.handleSpeakingEndedEvent,
    )

    // Error handling
    webRTCClient.addEventListener(
      VoiceChatEventType.ERROR,
      this.handleErrorEvent,
    )
  }

  private cleanupWebRTCClient(): void {
    if (this.webRTCClient) {
      this.webRTCClient.disconnect()

      // Remove all event listeners
      this.webRTCClient.removeEventListener(
        VoiceChatEventType.SPEECH_CAPTURED,
        this.handleSpeechCapturedEvent,
      )

      this.webRTCClient.removeEventListener(
        VoiceChatEventType.USER_TRANSCRIPTION_COMPLETED,
        this.handleUserTranscriptionCompletedEvent,
      )

      this.webRTCClient.removeEventListener(
        VoiceChatEventType.ASSISTANT_RESPONSE_STARTED,
        this.handleAssistantResponseStartedEvent,
      )

      this.webRTCClient.removeEventListener(
        VoiceChatEventType.ASSISTANT_RESPONSE_DELTA,
        this.handleAssistantResponseDeltaEvent,
      )

      this.webRTCClient.removeEventListener(
        VoiceChatEventType.ASSISTANT_RESPONSE_DONE,
        this.handleAssistantResponseDoneEvent,
      )

      this.webRTCClient.removeEventListener(
        VoiceChatEventType.SPEAKING_STARTED,
        this.handleSpeakingStartedEvent,
      )

      this.webRTCClient.removeEventListener(
        VoiceChatEventType.SPEAKING_ENDED,
        this.handleSpeakingEndedEvent,
      )

      this.webRTCClient.removeEventListener(
        VoiceChatEventType.ERROR,
        this.handleErrorEvent,
      )

      this.webRTCClient = null
    }
  }

  // Listening state handlers

  private handleSpeechCapturedEvent = (
    event: WebRTCSpeechCapturedEvent,
  ): void => {
    if (!this.currentSession) return

    const { itemId } = event

    if (this.voiceThreadMessageByEvent[itemId]) {
      return
    }

    // Create a new streaming user message
    const messageIndex = this.rootStore.chat.createUncommittedMessage(
      'user',
      true,
    )

    runInAction(() => {
      this.audioState = AudioState.INACTIVE
      this.voiceThreadMessageByEvent[itemId] = {
        index: messageIndex,
        fullMessage: '',
      }
    })
  }

  // User transcription handlers

  private handleUserTranscriptionCompletedEvent = (
    event: WebRTCUserTranscriptionCompletedEvent,
  ): void => {
    const { text, itemId } = event

    const messageIndex = this.voiceThreadMessageByEvent[itemId]

    if (messageIndex === undefined) {
      console.warn(`No message index found for itemId: ${itemId}`)
      return
    }

    this.rootStore.chat.commitMessage(messageIndex.index, text)
  }

  private handleAssistantResponseStartedEvent = (
    event: WebRTCAssistantResponseStartedEvent,
  ): void => {
    if (!this.currentSession) return

    const { itemId } = event

    const messageIndex = this.rootStore.chat.createUncommittedMessage(
      'assistant',
      true,
    )

    runInAction(() => {
      this.rootStore.tool.generating = true
      this.voiceThreadMessageByEvent[itemId] = {
        index: messageIndex,
        fullMessage: '',
      }
    })
  }

  private handleAssistantResponseDeltaEvent = (
    event: WebRTCAssistantResponseDeltaEvent,
  ): void => {
    if (!this.currentSession) return

    const { itemId } = event

    const voiceThreadMessage = this.voiceThreadMessageByEvent[itemId]

    if (voiceThreadMessage === undefined) {
      console.warn(`No message index found for itemId: ${itemId}`)
      return
    }

    runInAction(() => {
      voiceThreadMessage.fullMessage += event.delta
    })
  }

  private handleAssistantResponseDoneEvent = (
    event: WebRTCAssistantResponseDoneEvent,
  ): void => {
    if (!this.currentSession) return

    const { text, itemId } = event

    const voiceThreadMessage = this.voiceThreadMessageByEvent[itemId]

    if (voiceThreadMessage === undefined) {
      console.warn(`No message index found for itemId: ${itemId}`)
      return
    }

    voiceThreadMessage.fullMessage = text

    // Finalize the assistant message
  }

  // Speaking state handlers
  private handleSpeakingStartedEvent = (): void => {
    if (!this.currentSession) return

    runInAction(() => {
      this.audioState = AudioState.SPEAKING
    })
  }

  private handleSpeakingEndedEvent = (
    event: WebRTCSpeakingEndedEvent,
  ): void => {
    if (!this.currentSession) return

    const { itemId } = event

    const voiceThreadMessage = this.voiceThreadMessageByEvent[itemId]

    if (voiceThreadMessage === undefined) {
      console.warn(`No message index found for itemId: ${itemId}`)
      return
    }

    this.rootStore.chat.commitMessage(
      voiceThreadMessage.index,
      voiceThreadMessage.fullMessage,
    )

    runInAction(() => {
      this.audioState = AudioState.LISTENING

      // NOTE: We consider generation as over when the assistant is done speaking (also when interrupted)
      this.rootStore.tool.generating = false
    })
  }

  private handleErrorEvent = (event: WebRTCErrorEvent): void => {
    console.error('WebRTC error:', event.error, event.details)

    runInAction(() => {
      this.error = event.error || 'Unknown WebRTC error'

      if (this.state === VoiceChatState.ACTIVE) {
        this.state = VoiceChatState.ERROR
        toast.error('Error en la comunicación de voz')

        this.endSession()
      }
    })
  }
}
